/**
 * Business vs business — the group's performance in one currency.
 *
 * Reads GET /group-reports/performance. Every money figure arrives already
 * converted to `currency` on the server, so it is formatted, never converted
 * again here. Growth and margins are worked out on each business's own
 * figures (they're the same in any currency).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDownUp,
  ArrowRight,
  Award,
  Gauge,
  Loader2,
  Percent,
  Rocket,
  ShoppingBag,
  TrendingUp,
  Trophy,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { useGroupPerformance, type CompareWith, type GroupPerformanceResponse, type Highlight, type PerformanceRow } from "@/hooks/useGroupReports";
import useBusinessStore from "@/stores/businessStore";
import { cn } from "@/lib/utils";
import FxMoney, { fmtIn, fmtPct, Growth } from "./FxMoney";

const PALETTE = ["#4CAF50", "#0A84FF", "#F59E0B", "#8B5CF6", "#EF4444", "#14B8A6", "#EC4899", "#64748B"];

type Metric = "revenueMinor" | "netProfitMinor" | "grossProfitMinor" | "netMarginPct" | "revenueGrowthPct" | "cashInMinor";
const METRICS: { key: Metric; label: string; money: boolean }[] = [
  { key: "revenueMinor", label: "Sales", money: true },
  { key: "netProfitMinor", label: "Net profit", money: true },
  { key: "grossProfitMinor", label: "Gross profit", money: true },
  { key: "netMarginPct", label: "Net margin", money: false },
  { key: "revenueGrowthPct", label: "Growth", money: false },
  { key: "cashInMinor", label: "Money in", money: true },
];

type SortKey = Metric | "rank" | "orders" | "avgSaleMinor" | "businessName";

const industryLabel = (id: string | null) =>
  id ? id.charAt(0).toUpperCase() + id.slice(1).replace(/_/g, " ") : "";

export default function GroupPerformance({ from, to, compare, currency }: { from: string; to: string; compare: CompareWith; currency: string }) {
  const q = useGroupPerformance({ from, to, compare, currency });
  if (q.isLoading) {
    return (
      <div className="rounded-xl border bg-card py-12 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" aria-hidden />
        Comparing your businesses…
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className="rounded-xl border border-rose-300/40 bg-rose-50/50 py-6 text-center dark:bg-rose-950/20" role="alert">
        <AlertTriangle className="mx-auto mb-2 h-5 w-5 text-rose-500" aria-hidden />
        <p className="text-sm text-rose-700 dark:text-rose-300">Couldn't load the comparison.</p>
        <Button variant="outline" size="sm" className="mt-3" onClick={() => q.refetch()}>
          Try again
        </Button>
      </div>
    );
  }
  return <PerformanceBody data={q.data} refreshing={q.isFetching} />;
}

function PerformanceBody({ data, refreshing }: { data: GroupPerformanceResponse; refreshing: boolean }) {
  const cur = data.currency;
  const t = data.totals;
  const many = data.businessCount > 1;
  const colorOf = useMemo(() => {
    const m = new Map<string, string>();
    data.businesses.forEach((b, i) => m.set(b.businessId, PALETTE[i % PALETTE.length]));
    return (id: string) => m.get(id) || PALETTE[0];
  }, [data.businesses]);

  return (
    <div className={cn("space-y-5 transition-opacity", refreshing && "opacity-70")} aria-busy={refreshing}>
      <p className="text-xs text-muted-foreground">
        {data.businessCount} business{data.businessCount === 1 ? "" : "es"} · {data.period.from} to {data.period.to} · compared with {data.previous.label} ({data.previous.from} to {data.previous.to}) · in {cur}
      </p>

      {(data.unconverted.length > 0 || data.failedBusinesses.length > 0 || data.ratesStale) && (
        <div className="rounded-xl border border-amber-300/50 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200" role="status">
          {data.unconverted.length > 0 && (
            <p>
              Not in the totals (no {cur} rate today): {data.unconverted.map((u) => `${u.businessName} (${u.currency})`).join(", ")}. Their own figures are in the table.
            </p>
          )}
          {data.failedBusinesses.length > 0 && <p>Couldn't read: {data.failedBusinesses.map((f) => f.businessName).join(", ")}. Try again in a moment.</p>}
          {data.ratesStale && <p>Exchange rates are from the last good update, not today.</p>}
        </div>
      )}

      {/* Group totals */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi icon={TrendingUp} label="Sales" value={fmtIn(t.revenueMinor, cur)} sub={<Growth pct={t.revenueGrowthPct} hadBefore={t.previousRevenueMinor !== 0} />} />
        <Kpi icon={Gauge} label="Gross profit" value={fmtIn(t.grossProfitMinor, cur)} sub={<span>{fmtPct(t.grossMarginPct)} of sales</span>} />
        <Kpi
          icon={Wallet}
          label="Net profit"
          value={fmtIn(t.netProfitMinor, cur)}
          tone={t.netProfitMinor < 0 ? "neg" : "pos"}
          sub={<span>{fmtPct(t.netMarginPct)} margin · <Growth pct={t.profitGrowthPct} hadBefore={t.previousNetProfitMinor !== 0} /></span>}
        />
        <Kpi icon={ShoppingBag} label="Orders" value={t.orders.toLocaleString("en-US")} sub={<span>Avg sale {fmtIn(t.avgSaleMinor, cur)}</span>} />
        <Kpi icon={ArrowDownUp} label="Money in" value={fmtIn(t.cashInMinor, cur)} sub={<span>Cash and bank received</span>} className="col-span-2 lg:col-span-1" />
      </div>

      {many && <Highlights data={data} />}
      {many && <CompareChart data={data} colorOf={colorOf} />}
      <CompareTable data={data} colorOf={colorOf} />
      {many && <TrendChart data={data} colorOf={colorOf} />}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tone, className }: { icon: LucideIcon; label: string; value: string; sub?: React.ReactNode; tone?: "pos" | "neg"; className?: string }) {
  return (
    <div className={cn("min-w-0 rounded-xl border bg-card px-4 py-3", className)}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
      </div>
      <div className={cn("truncate text-lg font-bold tabular-nums sm:text-xl", tone === "neg" && "text-rose-600 dark:text-rose-400")}>{value}</div>
      {sub && <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

// ── Best performing & friends ───────────────────────────────────────

function Highlights({ data }: { data: GroupPerformanceResponse }) {
  const h = data.highlights;
  const cur = data.currency;
  const best = h.bestPerforming;
  const bestRow = best ? data.businesses.find((b) => b.businessId === best.businessId) : null;
  const others: { key: string; icon: LucideIcon; title: string; hl: Highlight | null; show: (v: number | null) => string; tone?: "warn" }[] = [
    { key: "top", icon: Award, title: "Most sales", hl: h.topRevenue, show: (v) => fmtIn(v, cur) },
    { key: "margin", icon: Percent, title: "Best margin", hl: h.bestMargin, show: (v) => `${fmtPct(v)} net` },
    { key: "growth", icon: Rocket, title: "Fastest growing", hl: h.fastestGrowing, show: (v) => `${fmtPct(v, true)} sales` },
    {
      key: "attention",
      icon: AlertTriangle,
      title: "Needs attention",
      hl: h.needsAttention,
      show: (v) => (h.needsAttention?.reason.startsWith("Losing") ? fmtIn(v, cur) : `${fmtPct(v, true)} sales`),
      tone: "warn",
    },
  ];
  return (
    <section aria-label="Highlights" className="grid gap-3 lg:grid-cols-[minmax(0,1.3fr)_minmax(0,2fr)]">
      <div className="relative overflow-hidden rounded-2xl border border-[#4CAF50]/40 bg-gradient-to-br from-[#4CAF50]/15 to-transparent p-5">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#2E7D32] dark:text-[#81C784]">
          <Trophy className="h-4 w-4" aria-hidden /> Best performing
        </div>
        {best && bestRow ? (
          <>
            <div className="mt-2 truncate text-2xl font-semibold" data-testid="best-business">
              {best.businessName}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              <FxMoney minor={best.value} currency={cur} className="font-semibold text-foreground" /> net profit · {fmtPct(bestRow.netMarginPct)} margin
              {bestRow.profitSharePct != null && <> · {fmtPct(bestRow.profitSharePct)} of the group's profit</>}
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Sales <Growth pct={bestRow.revenueGrowthPct} hadBefore={(bestRow.previous.revenueMinor ?? 0) !== 0} /> vs {data.previous.label}
            </div>
          </>
        ) : (
          <p className="mt-2 text-sm text-muted-foreground">No business has made a profit in this period yet.</p>
        )}
        <p className="mt-3 text-[11px] text-muted-foreground">Best performing = the most net profit (sales less every cost) in the period.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {others.map((o) => (
          <div
            key={o.key}
            className={cn(
              "min-w-0 rounded-xl border bg-card p-3",
              o.tone === "warn" && o.hl && "border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20",
            )}
          >
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <o.icon className={cn("h-3.5 w-3.5", o.tone === "warn" && o.hl && "text-amber-600")} aria-hidden /> {o.title}
            </div>
            {o.hl ? (
              <>
                <div className="mt-1 truncate text-sm font-semibold">{o.hl.businessName}</div>
                <div className="truncate text-xs text-muted-foreground">
                  {o.show(o.hl.value)} · {o.hl.reason}
                </div>
              </>
            ) : (
              <div className="mt-1 text-xs text-muted-foreground">{o.tone === "warn" ? "Nothing worrying" : "Not enough to compare yet"}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Side-by-side bar chart ──────────────────────────────────────────

function CompareChart({ data, colorOf }: { data: GroupPerformanceResponse; colorOf: (id: string) => string }) {
  const [metric, setMetric] = useState<Metric>("revenueMinor");
  const m = METRICS.find((x) => x.key === metric)!;
  const rows = data.businesses
    .filter((b) => (m.money ? b.converted : true) && b[metric] != null)
    .map((b) => ({ id: b.businessId, name: b.businessName, value: m.money ? (b[metric] as number) / 100 : (b[metric] as number) }))
    .sort((a, b) => b.value - a.value);
  const show = (v: number) => (m.money ? fmtIn(Math.round(v * 100), data.currency, { short: true }) : `${v.toFixed(1)}%`);
  return (
    <section className="rounded-2xl border bg-card p-4" aria-label="Compare businesses">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Side by side</h3>
        <div className="flex flex-wrap gap-1" role="group" aria-label="Compare on">
          {METRICS.map((x) => (
            <button
              key={x.key}
              type="button"
              aria-pressed={metric === x.key}
              onClick={() => setMetric(x.key)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                metric === x.key ? "border-[#4CAF50] bg-[#4CAF50]/10 font-medium" : "text-muted-foreground hover:bg-muted",
              )}
            >
              {x.label}
            </button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Nothing to compare for this period.</p>
      ) : (
        <div style={{ height: Math.max(140, rows.length * 44) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ left: 4, right: 16, top: 4, bottom: 4 }}>
              <CartesianGrid horizontal={false} strokeDasharray="3 3" className="stroke-border" />
              <XAxis type="number" tickFormatter={show} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v: number) => [show(v), m.label]} cursor={{ fillOpacity: 0.08 }} />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive={false}>
                {rows.map((r) => (
                  <Cell key={r.id} fill={r.value < 0 ? "#EF4444" : colorOf(r.id)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

// ── The table ───────────────────────────────────────────────────────

function CompareTable({ data, colorOf }: { data: GroupPerformanceResponse; colorOf: (id: string) => string }) {
  const navigate = useNavigate();
  const setSlug = useBusinessStore((s) => s.setActiveBusinessSlug);
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "rank", dir: 1 });
  const cur = data.currency;
  const rows = useMemo(() => {
    const v = (r: PerformanceRow) => (sort.key === "businessName" ? r.businessName : (r[sort.key] as number | null));
    return [...data.businesses].sort((a, b) => {
      const x = v(a);
      const y = v(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return (typeof x === "string" ? x.localeCompare(String(y)) : x - (y as number)) * sort.dir;
    });
  }, [data.businesses, sort]);
  const open = (slug: string) => {
    setSlug(slug);
    navigate("/dashboard");
  };
  const Th = ({ k, children, className }: { k: SortKey; children: React.ReactNode; className?: string }) => (
    <th scope="col" className={cn("whitespace-nowrap px-3 py-2 font-medium", className)} aria-sort={sort.key === k ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => setSort((s) => (s.key === k ? { key: k, dir: (s.dir * -1) as 1 | -1 } : { key: k, dir: k === "rank" || k === "businessName" ? 1 : -1 }))}
      >
        {children}
        {sort.key === k && <span aria-hidden>{sort.dir === 1 ? "↑" : "↓"}</span>}
      </button>
    </th>
  );
  const own = (r: PerformanceRow, minor: number) => (r.converted && r.currency === cur ? null : <div className="text-[10px] text-muted-foreground">{fmtIn(minor, r.currency)}</div>);

  return (
    <section className="rounded-2xl border bg-card" aria-label="Business by business">
      <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Business by business</h3>
        <span className="text-[11px] text-muted-foreground">Ranked by net profit · tap a heading to sort</span>
      </div>

      {/* Phones: one card per business */}
      <ul className="divide-y md:hidden">
        {rows.map((r) => (
          <li key={r.businessId} className="px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colorOf(r.businessId) }} aria-hidden />
                  <span className="truncate font-medium">
                    {r.rank ? `${r.rank}. ` : ""}
                    {r.businessName}
                  </span>
                </div>
                <div className="text-[11px] text-muted-foreground">
                  {industryLabel(r.industry)} · {r.currency}
                </div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => open(r.slug)} aria-label={`Open ${r.businessName}`}>
                Open <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden />
              </Button>
            </div>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <dt className="text-muted-foreground">Sales</dt>
              <dd className="text-right tabular-nums">
                {r.converted ? fmtIn(r.revenueMinor, cur) : fmtIn(r.own.revenueMinor, r.currency)} <Growth pct={r.revenueGrowthPct} hadBefore={(r.previous.revenueMinor ?? r.own.revenueMinor) !== 0} />
              </dd>
              <dt className="text-muted-foreground">Net profit</dt>
              <dd className="text-right tabular-nums">
                <FxMoney minor={r.converted ? r.netProfitMinor : r.own.netProfitMinor} currency={r.converted ? cur : r.currency} signed /> · {fmtPct(r.netMarginPct)}
              </dd>
              <dt className="text-muted-foreground">Gross margin</dt>
              <dd className="text-right tabular-nums">{fmtPct(r.grossMarginPct)}</dd>
              <dt className="text-muted-foreground">Orders · avg</dt>
              <dd className="text-right tabular-nums">
                {r.orders} · {r.converted ? fmtIn(r.avgSaleMinor, cur) : fmtIn(r.own.avgSaleMinor, r.currency)}
              </dd>
            </dl>
          </li>
        ))}
      </ul>

      {/* Tablets and up: the full table */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-muted-foreground">
            <tr className="border-b">
              <Th k="rank">#</Th>
              <Th k="businessName">Business</Th>
              <Th k="revenueMinor" className="text-right">Sales</Th>
              <Th k="revenueGrowthPct" className="text-right">Growth</Th>
              <Th k="grossProfitMinor" className="text-right">Gross profit</Th>
              <Th k="netProfitMinor" className="text-right">Net profit</Th>
              <Th k="netMarginPct" className="text-right">Margin</Th>
              <Th k="orders" className="text-right">Orders</Th>
              <Th k="avgSaleMinor" className="text-right">Avg sale</Th>
              <Th k="cashInMinor" className="text-right">Money in</Th>
              <th scope="col" className="whitespace-nowrap px-3 py-2 font-medium">Share</th>
              <th scope="col" className="px-3 py-2"><span className="sr-only">Open</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.businessId} className="border-b last:border-0 hover:bg-muted/40">
                <td className="px-3 py-2 tabular-nums text-muted-foreground">{r.rank ?? "—"}</td>
                <td className="max-w-[14rem] px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: colorOf(r.businessId) }} aria-hidden />
                    <span className="truncate font-medium">{r.businessName}</span>
                    {data.highlights.bestPerforming?.businessId === r.businessId && <Trophy className="h-3.5 w-3.5 shrink-0 text-[#4CAF50]" aria-label="Best performing" />}
                  </div>
                  <div className="pl-[18px] text-[11px] text-muted-foreground">
                    {industryLabel(r.industry)} · {r.currency}
                    {!r.converted && " · not in totals"}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  {r.converted ? <FxMoney minor={r.revenueMinor} currency={cur} /> : <FxMoney minor={r.own.revenueMinor} currency={r.currency} />}
                  {r.converted && own(r, r.own.revenueMinor)}
                </td>
                <td className="px-3 py-2 text-right">
                  <Growth pct={r.revenueGrowthPct} hadBefore={(r.previous.revenueMinor ?? 1) !== 0} />
                </td>
                <td className="px-3 py-2 text-right">
                  <FxMoney minor={r.converted ? r.grossProfitMinor : r.own.grossProfitMinor} currency={r.converted ? cur : r.currency} signed />
                  <div className="text-[10px] text-muted-foreground">{fmtPct(r.grossMarginPct)}</div>
                </td>
                <td className="px-3 py-2 text-right font-medium">
                  <FxMoney minor={r.converted ? r.netProfitMinor : r.own.netProfitMinor} currency={r.converted ? cur : r.currency} signed />
                  <div className="text-[10px] font-normal">
                    <Growth pct={r.profitGrowthPct} hadBefore={(r.previous.netProfitMinor ?? 1) !== 0} />
                  </div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtPct(r.netMarginPct)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.orders.toLocaleString("en-US")}</td>
                <td className="px-3 py-2 text-right">
                  <FxMoney minor={r.converted ? r.avgSaleMinor : r.own.avgSaleMinor} currency={r.converted ? cur : r.currency} />
                </td>
                <td className="px-3 py-2 text-right">
                  <FxMoney minor={r.converted ? r.cashInMinor : r.own.cashInMinor} currency={r.converted ? cur : r.currency} />
                </td>
                <td className="w-28 px-3 py-2">
                  {r.revenueSharePct != null ? (
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted" aria-hidden>
                        <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, r.revenueSharePct))}%`, background: colorOf(r.businessId) }} />
                      </div>
                      <span className="w-10 text-right text-xs tabular-nums">{fmtPct(r.revenueSharePct)}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => open(r.slug)} aria-label={`Open ${r.businessName}`}>
                    Open
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
          {data.businessCount > 1 && (
            <tfoot className="border-t bg-muted/30 text-sm font-medium">
              <tr>
                <td className="px-3 py-2" />
                <td className="px-3 py-2">Group</td>
                <td className="px-3 py-2 text-right"><FxMoney minor={data.totals.revenueMinor} currency={cur} /></td>
                <td className="px-3 py-2 text-right"><Growth pct={data.totals.revenueGrowthPct} hadBefore={data.totals.previousRevenueMinor !== 0} /></td>
                <td className="px-3 py-2 text-right"><FxMoney minor={data.totals.grossProfitMinor} currency={cur} signed /></td>
                <td className="px-3 py-2 text-right"><FxMoney minor={data.totals.netProfitMinor} currency={cur} signed /></td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtPct(data.totals.netMarginPct)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{data.totals.orders.toLocaleString("en-US")}</td>
                <td className="px-3 py-2 text-right"><FxMoney minor={data.totals.avgSaleMinor} currency={cur} /></td>
                <td className="px-3 py-2 text-right"><FxMoney minor={data.totals.cashInMinor} currency={cur} /></td>
                <td className="px-3 py-2" colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

// ── Sales over the period, one line per business ────────────────────

function TrendChart({ data, colorOf }: { data: GroupPerformanceResponse; colorOf: (id: string) => string }) {
  const lines = data.businesses.filter((b) => b.converted && b.series.length > 0);
  const points = useMemo(() => {
    const keys = [...new Set(lines.flatMap((b) => b.series.map((p) => p.key)))].sort();
    return keys.map((k) => {
      const row: Record<string, number | string> = { key: k };
      for (const b of lines) row[b.businessId] = (b.series.find((p) => p.key === k)?.revenueMinor ?? 0) / 100;
      return row;
    });
  }, [lines]);
  if (points.length < 2) return null;
  const label = data.period.bucket === "month" ? "Monthly" : data.period.bucket === "week" ? "Weekly" : "Daily";
  return (
    <section className="rounded-2xl border bg-card p-4" aria-label="Sales over time">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{label} sales</h3>
        <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground">
          {lines.map((b) => (
            <span key={b.businessId} className="inline-flex items-center gap-1">
              <span className="h-2 w-2 rounded-full" style={{ background: colorOf(b.businessId) }} aria-hidden />
              {b.businessName}
            </span>
          ))}
        </div>
      </div>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ left: 0, right: 8, top: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
            <XAxis dataKey="key" tick={{ fontSize: 10 }} tickFormatter={(k: string) => (data.period.bucket === "month" ? k : k.slice(5))} minTickGap={16} />
            <YAxis tick={{ fontSize: 10 }} width={56} tickFormatter={(v: number) => fmtIn(Math.round(v * 100), data.currency, { short: true })} />
            <Tooltip
              formatter={(v: number, id: string) => [fmtIn(Math.round(v * 100), data.currency), lines.find((b) => b.businessId === id)?.businessName ?? id]}
            />
            {lines.map((b) => (
              <Line key={b.businessId} type="monotone" dataKey={b.businessId} stroke={colorOf(b.businessId)} strokeWidth={2} dot={false} isAnimationActive={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
